/* =====================================================================
 * BlockEditor — 범용 블록 에디터 (노션 스타일 TipTap)
 *
 * 기존 components/document/DocumentEditor.tsx 에서 "제목(TitleArea)과
 * 저장 로직을 제외한 순수 블록 편집 부분"을 추출한 공용 컴포넌트입니다.
 *
 * 확장성:
 *   - value/onChange 로 외부 상태와 분리
 *   - additionalExtensions 로 포맷 특화 확장(FontFamily 등) 주입
 *   - editor 인스턴스를 ref/콜백으로 외부 노출
 *   - 선택 변화를 SelectionAdapter 로 정규화
 * ===================================================================== */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useEditor, EditorContent, type Editor, type JSONContent } from '@tiptap/react';
import type { Extension } from '@tiptap/core';
import DragHandle from '@tiptap/extension-drag-handle-react';
import { offset } from '@floating-ui/dom';
import { NodeSelection } from '@tiptap/pm/state';
import { DropdownMenu, type DropdownMenuItem } from '@auw/workspace-ui';
import { useFeatureFlags } from '@auw/feature-flags';

import {
  getEditorExtensions,
  detectCodeLanguage,
  labelToLowlightId,
} from '../../document/extensions';
import { useDragHandle } from '../../document/useDragHandle';
import { useSlashMenu, renderMenuIcon } from '../../document/useSlashMenu';
import { useTableControls } from '../../document/useTableControls';
import { useTableCellMenu } from '../../document/useTableCellMenu';
import {
  EditorBody,
  EditorArea,
  HandleRow,
  TableHandleRow,
  HandleBtn,
  SlashDropdown,
  DropdownScroll,
  DropdownSection,
  DropdownItem,
  DropdownIcon,
  DropdownLabel,
  DropdownShortcut,
  DropdownFooter,
} from '../../document/editor.styles';

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

export interface BlockEditorSelectionInfo {
  /** ProseMirror selection anchor */
  anchor: number;
  /** ProseMirror selection head */
  head: number;
  /** 공통 블록 ID 목록 (NodeSelection 또는 TextSelection 범위) */
  blockIds: string[];
  /** 블록에 포함된 가장 가까운 top-level 노드의 텍스트 미리보기 */
  preview: string;
  /** 선택이 비어있는지(커서만) */
  empty: boolean;
}

export interface BlockEditorHandle {
  /** TipTap Editor 인스턴스 획득 */
  getEditor: () => Editor | null;
}

export interface BlockEditorProps {
  value: JSONContent;
  onChange?: (value: JSONContent) => void;
  /** 디바운스(ms). 기본 500 — 0 이하이면 즉시 호출 */
  debounceMs?: number;
  /** 읽기 전용 */
  readOnly?: boolean;
  /** 추가 확장 (FontFamily 등 포맷 특화) */
  additionalExtensions?: Extension[];
  /** editor ready 콜백 */
  onEditorReady?: (editor: Editor) => void;
  /** 선택 변화 콜백 */
  onSelectionChange?: (info: BlockEditorSelectionInfo) => void;
  /** 에디터 DOM의 추가 클래스 */
  editorClassName?: string;
  /** 값 교체 시 기준이 되는 식별자(바뀌면 setContent) */
  contentKey?: string;
  /** 에디터 영역 아래에 렌더할 추가 UI (툴팁/플로팅 버튼) */
  overlay?: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

function extractSelectionInfo(editor: Editor): BlockEditorSelectionInfo {
  const { state } = editor;
  const { selection } = state;
  const empty = selection.empty;
  const anchor = selection.anchor;
  const head = selection.head;

  const blockIds: string[] = [];
  const texts: string[] = [];
  try {
    const from = Math.min(anchor, head);
    const to = Math.max(anchor, head);
    state.doc.nodesBetween(from, to, (node, pos, parent) => {
      if (!parent) return true;
      if (parent.type.name !== 'doc') return true;
      const id = (node.attrs as Record<string, unknown> | undefined)?.['blockId'] as string | undefined;
      if (id) blockIds.push(id);
      texts.push(node.textContent.slice(0, 80));
      return false;
    });
  } catch {
    /* noop */
  }
  const preview = texts.join(' · ').slice(0, 160);

  return { anchor, head, blockIds, preview, empty };
}

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

export const BlockEditor = forwardRef<BlockEditorHandle, BlockEditorProps>(function BlockEditor(
  {
    value,
    onChange,
    debounceMs = 500,
    readOnly = false,
    additionalExtensions,
    onEditorReady,
    onSelectionChange,
    editorClassName,
    contentKey,
    overlay,
  },
  ref,
) {
  const editorBodyRef = useRef<HTMLDivElement>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const autoDetectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [codeLangMenu, setCodeLangMenu] = useState<{
    open: boolean;
    x: number;
    y: number;
    direction: 'down' | 'up';
    pos: number | null;
  }>({ open: false, x: 0, y: 0, direction: 'down', pos: null });

  const CODE_LANGUAGES = useMemo(
    () => ['Plain text', 'JavaScript', 'TypeScript', 'Python', 'SQL', 'JSON', 'Bash'],
    [],
  );

  /* P11-5g: `mode.doc-agent.mediaEmbed` toggles whether the TipTap
   * media nodes (image/video/audio/file/bookmark) are registered. We
   * also use the flag to suppress the slash-menu media entries below. */
  const { isEnabled } = useFeatureFlags();
  const mediaEmbedEnabled = isEnabled('mode.doc-agent.mediaEmbed');

  const extensions = useMemo(() => {
    const base = getEditorExtensions({ mediaEmbed: mediaEmbedEnabled });
    return additionalExtensions ? [...base, ...additionalExtensions] : base;
  }, [additionalExtensions, mediaEmbedEnabled]);

  const editor = useEditor({
    extensions,
    content: value,
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: `doc-editor-content ${editorClassName ?? ''}`.trim(),
        spellcheck: 'false',
      },
    },
    onUpdate: ({ editor: e }) => {
      if (!onChange) return;
      if (debounceMs > 0) {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
          onChange(e.getJSON());
        }, debounceMs);
      } else {
        onChange(e.getJSON());
      }
    },
    onSelectionUpdate: ({ editor: e }) => {
      if (!onSelectionChange) return;
      onSelectionChange(extractSelectionInfo(e));
    },
  });

  useImperativeHandle(
    ref,
    () => ({
      getEditor: () => editor,
    }),
    [editor],
  );

  /* ── editor ready 콜백 ── */
  useEffect(() => {
    if (editor && onEditorReady) onEditorReady(editor);
  }, [editor, onEditorReady]);

  /* ── 외부 value 교체 시 에디터 내용 동기화 ── */
  useEffect(() => {
    if (!editor) return;
    const currentJson = JSON.stringify(editor.getJSON());
    const newJson = JSON.stringify(value);
    if (currentJson !== newJson) {
      editor.commands.setContent(value, { emitUpdate: false } as never);
    }
    // contentKey가 변할 때만 명시적으로 재설정하기 위해 의존성 제한
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey]);

  /* ── 읽기 전용 상태 동기화 ── */
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  /* ── DragHandle / SlashMenu / Table / CellMenu ── */
  const {
    nodeInfoRef,
    handleInsideTable,
    setHandleInsideTable,
    tableHandleStyle,
    hoveringCustomHandleRef,
    tableHideTimerRef,
    handleNodeChange,
    handleGripClick,
    nestedConfig,
  } = useDragHandle({ editor, editorBodyRef });

  const {
    slashMenuOpen,
    slashMenuPos,
    slashMenuDirection,
    slashMenuRef,
    filteredSections,
    selectedIndex,
    executeSlashCommand,
    closeSlashMenu,
    handlePlusBtnClick,
  } = useSlashMenu({ editor, nodeInfoRef, editorBodyRef, mediaEmbed: mediaEmbedEnabled });

  const { tableDragRef } = useTableControls({ editor });
  const { cellMenuState, closeCellMenu, getCellMenuItems } = useTableCellMenu({ editor });

  const closeCodeLangMenu = useCallback(() => {
    setCodeLangMenu((prev) => ({ ...prev, open: false, pos: null }));
  }, []);

  const getCodeBlockPosFromDom = useCallback(
    (el: HTMLElement): number | null => {
      if (!editor) return null;
      let matchedPos: number | null = null;
      editor.state.doc.descendants((node, pos) => {
        if (matchedPos !== null) return false;
        if (node.type.name !== 'codeBlock') return true;
        const dom = editor.view.nodeDOM(pos);
        if (dom === el) {
          matchedPos = pos;
          return false;
        }
        return true;
      });
      if (matchedPos !== null) return matchedPos;

      const resolveCodeBlockPos = (pos: number): number | null => {
        try {
          const maxPos = editor.state.doc.content.size;
          const clamped = Math.max(1, Math.min(pos, Math.max(1, maxPos - 1)));
          const $pos = editor.state.doc.resolve(clamped);
          for (let d = $pos.depth; d >= 1; d -= 1) {
            if ($pos.node(d).type.name === 'codeBlock') return $pos.before(d);
          }
        } catch {
          /* noop */
        }
        return null;
      };

      try {
        const rawPos = editor.view.posAtDOM(el, 0);
        return (
          resolveCodeBlockPos(rawPos + 1)
          ?? resolveCodeBlockPos(rawPos)
          ?? resolveCodeBlockPos(rawPos - 1)
        );
      } catch {
        return null;
      }
    },
    [editor],
  );

  const applyCodeLanguage = useCallback(
    (language: string) => {
      if (!editor || codeLangMenu.pos === null) return;
      const targetPos = codeLangMenu.pos;
      const lowlightId = labelToLowlightId(language);
      let applied = false;
      try {
        const tr = editor.state.tr.setSelection(
          NodeSelection.create(editor.state.doc, targetPos),
        );
        const selectedPos = tr.selection.from;
        const node = tr.doc.nodeAt(selectedPos);
        if (node && node.type.name === 'codeBlock') {
          tr.setNodeMarkup(selectedPos, undefined, {
            ...node.attrs,
            language: lowlightId || language,
          });
          editor.view.dispatch(tr);
          applied = true;
        }
      } catch {
        /* fallback */
      }
      if (!applied) {
        editor.chain().focus(targetPos).updateAttributes('codeBlock', { language }).run();
      }
      closeCodeLangMenu();
    },
    [editor, codeLangMenu.pos, closeCodeLangMenu],
  );

  const codeLanguageItems: DropdownMenuItem[] = useMemo(
    () => CODE_LANGUAGES.map((language) => ({
      id: language,
      label: language,
      onClick: () => applyCodeLanguage(language),
    })),
    [CODE_LANGUAGES, applyCodeLanguage],
  );

  /* ── 코드블록 언어 자동 감지 ── */
  useEffect(() => {
    if (!editor) return;
    const autoDetect = () => {
      if (autoDetectTimerRef.current) clearTimeout(autoDetectTimerRef.current);
      autoDetectTimerRef.current = setTimeout(() => {
        const { doc } = editor.state;
        let needsDispatch = false;
        const tr = editor.state.tr;
        doc.descendants((node, pos) => {
          if (node.type.name !== 'codeBlock') return true;
          if (node.attrs.language !== 'Plain text') return true;
          const content = node.textContent;
          if (!content || content.trim().length < 8) return true;
          const detected = detectCodeLanguage(content);
          if (detected && detected !== 'Plain text') {
            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              language: labelToLowlightId(detected),
            });
            needsDispatch = true;
          }
          return true;
        });
        if (needsDispatch) {
          tr.setMeta('addToHistory', false);
          editor.view.dispatch(tr);
        }
      }, 800);
    };
    editor.on('update', autoDetect);
    autoDetect();
    return () => {
      editor.off('update', autoDetect);
      if (autoDetectTimerRef.current) clearTimeout(autoDetectTimerRef.current);
    };
  }, [editor]);

  /* ── 코드블록 언어 라벨/복사 버튼 이벤트 ── */
  useEffect(() => {
    if (!editor) return;
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const pre = target.closest('pre.doc-code-block') as HTMLElement | null;
      if (!pre) return;
      const rect = pre.getBoundingClientRect();
      const inHeaderY = event.clientY >= rect.top + 2 && event.clientY <= rect.top + 30;
      if (!inHeaderY) return;
      const inCopyArea = event.clientX >= rect.right - 36 && event.clientX <= rect.right - 8;
      if (inCopyArea) {
        event.preventDefault();
        event.stopPropagation();
        const code = pre.querySelector('code');
        const text = code ? code.textContent || '' : pre.textContent || '';
        navigator.clipboard.writeText(text).then(() => {
          pre.classList.add('doc-code-copied');
          setTimeout(() => pre.classList.remove('doc-code-copied'), 1500);
        });
        return;
      }
      const inLangArea = event.clientX >= rect.right - 180 && event.clientX < rect.right - 36;
      if (!inLangArea) return;
      event.preventDefault();
      event.stopPropagation();
      const codePos = getCodeBlockPosFromDom(pre);
      if (codePos === null) return;
      const direction: 'down' | 'up' = rect.top + 230 > window.innerHeight ? 'up' : 'down';
      setCodeLangMenu({
        open: true,
        x: rect.right - 180,
        y: direction === 'down' ? rect.top + 30 : rect.top - 6,
        direction,
        pos: codePos,
      });
    };
    editor.view.dom.addEventListener('mousedown', onMouseDown, true);
    return () => editor.view.dom.removeEventListener('mousedown', onMouseDown, true);
  }, [editor, getCodeBlockPosFromDom]);

  if (!editor) return null;

  return (
    <EditorBody ref={editorBodyRef}>
      <DragHandle
        editor={editor}
        nested={nestedConfig}
        onNodeChange={handleNodeChange}
        computePositionConfig={{
          middleware: [offset({ mainAxis: 10, crossAxis: 9 })],
        }}
      >
        <HandleRow
          style={handleInsideTable ? { visibility: 'hidden', pointerEvents: 'none', height: 0, overflow: 'hidden' } : undefined}
        >
          <HandleBtn onClick={handlePlusBtnClick} title="블록 추가">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="6" y1="1" x2="6" y2="11" />
              <line x1="1" y1="6" x2="11" y2="6" />
            </svg>
          </HandleBtn>
          <HandleBtn onClick={handleGripClick} style={{ cursor: 'grab' }} title="드래그하여 이동">
            <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
              <circle cx="2" cy="2" r="1.5" />
              <circle cx="8" cy="2" r="1.5" />
              <circle cx="2" cy="8" r="1.5" />
              <circle cx="8" cy="8" r="1.5" />
              <circle cx="2" cy="14" r="1.5" />
              <circle cx="8" cy="14" r="1.5" />
            </svg>
          </HandleBtn>
        </HandleRow>
      </DragHandle>

      <EditorArea>
        <EditorContent editor={editor} />
      </EditorArea>

      <TableHandleRow
        $visible={handleInsideTable}
        style={handleInsideTable ? tableHandleStyle : undefined}
        onMouseEnter={() => {
          hoveringCustomHandleRef.current = true;
          if (tableHideTimerRef.current) {
            clearTimeout(tableHideTimerRef.current);
            tableHideTimerRef.current = null;
          }
          setHandleInsideTable(true);
        }}
        onMouseLeave={() => {
          hoveringCustomHandleRef.current = false;
          if (tableHideTimerRef.current) clearTimeout(tableHideTimerRef.current);
          tableHideTimerRef.current = setTimeout(() => {
            if (!hoveringCustomHandleRef.current) {
              setHandleInsideTable(false);
            }
          }, 300);
        }}
      >
        <HandleBtn onClick={handlePlusBtnClick} title="블록 추가">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <line x1="6" y1="1" x2="6" y2="11" />
            <line x1="1" y1="6" x2="11" y2="6" />
          </svg>
        </HandleBtn>
        <HandleBtn
          draggable
          onDragStart={(e) => {
            if (!editor) return;
            const { node, pos } = nodeInfoRef.current;
            if (!node) return;
            const tr = editor.state.tr.setSelection(
              NodeSelection.create(editor.state.doc, pos),
            );
            editor.view.dispatch(tr);
            tableDragRef.current = { node, pos };
            const slice = editor.state.selection.content();
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', '');
            (editor.view as unknown as { dragging: { slice: unknown; move: boolean } }).dragging = {
              slice,
              move: true,
            };
          }}
          onDragEnd={() => {
            tableDragRef.current = null;
          }}
          onClick={handleGripClick}
          style={{ cursor: 'grab' }}
          title="표 전체 선택 / 드래그"
        >
          <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
            <circle cx="2" cy="2" r="1.5" />
            <circle cx="8" cy="2" r="1.5" />
            <circle cx="2" cy="8" r="1.5" />
            <circle cx="8" cy="8" r="1.5" />
            <circle cx="2" cy="14" r="1.5" />
            <circle cx="8" cy="14" r="1.5" />
          </svg>
        </HandleBtn>
      </TableHandleRow>

      {slashMenuOpen && filteredSections.length > 0 && (
        <SlashDropdown
          ref={slashMenuRef}
          $direction={slashMenuDirection}
          style={{
            top: slashMenuDirection === 'down' ? slashMenuPos.topDown : undefined,
            bottom: slashMenuDirection === 'up'
              ? `calc(100% - ${slashMenuPos.topUp}px)`
              : undefined,
            left: slashMenuPos.left,
          }}
        >
          <DropdownScroll>
            {(() => {
              let runningIdx = 0;
              return filteredSections.map((section) => (
                <React.Fragment key={section.section}>
                  <DropdownSection>{section.section}</DropdownSection>
                  {section.items.map((item) => {
                    const idx = runningIdx++;
                    return (
                      <DropdownItem
                        key={item.id}
                        data-slash-index={idx}
                        $active={idx === selectedIndex}
                        onClick={() => executeSlashCommand(item.id)}
                      >
                        <DropdownIcon>{renderMenuIcon(item.icon)}</DropdownIcon>
                        <DropdownLabel>{item.label}</DropdownLabel>
                        {item.shortcut && <DropdownShortcut>{item.shortcut}</DropdownShortcut>}
                      </DropdownItem>
                    );
                  })}
                </React.Fragment>
              ));
            })()}
          </DropdownScroll>
          <DropdownFooter onClick={() => closeSlashMenu(true)}>
            <span>메뉴 닫기</span>
            <DropdownShortcut>esc</DropdownShortcut>
          </DropdownFooter>
        </SlashDropdown>
      )}

      <DropdownMenu
        open={codeLangMenu.open}
        x={codeLangMenu.x}
        y={codeLangMenu.y}
        items={codeLanguageItems}
        onClose={closeCodeLangMenu}
        direction={codeLangMenu.direction}
        align="left"
      />
      <DropdownMenu
        open={cellMenuState.open}
        x={cellMenuState.x}
        y={cellMenuState.y}
        items={getCellMenuItems()}
        onClose={closeCellMenu}
        direction={cellMenuState.direction}
        align="left"
      />

      {overlay}
    </EditorBody>
  );
});
